import React, { useState } from "react";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { FIREBASE_STORAGE } from "./firebase.config";
import {
  User,
  Book,
  Phone,
  Briefcase,
  Award,
  File,
  Plus,
  X,
  Mail,
  MapPin,
  Building,
  Globe,
  Calendar,
  FileText,
} from "lucide-react";
import { Link } from "react-router-dom";

export const API_URL =
  process.env.REACT_APP_API_URL || "https://sbg-backend.onrender.com";

const GeneralApplication = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    streetAddress: "",
    addressLine2: "",
    city: "",
    stateProvince: "",
    zipPostalCode: "",
    country: "",
    phone: "",
    education: [{ degree: "", institution: "", yearCompleted: "" }],
    references: [{ name: "", relationship: "", company: "", phone: "" }],
    previousEmployment: [
      {
        company: "",
        supervisor: "",
        phone: "",
        startingSalary: "",
        endingSalary: "",
        responsibilities: "",
        startDate: "",
        endDate: "",
        reasonForLeaving: "",
        mayContact: false,
      },
    ],
    militaryService: {
      branch: "",
      datesServed: "",
      rankAtDischarge: "",
      typeOfDischarge: "",
      dischargeExplanation: "",
    },
    termsAccepted: false,
  });

  const [resumeFile, setResumeFile] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  const handleInputChange = (e, index, section) => {
    const { name, value, type, checked } = e.target;
    
    if (section) {
      // Handle array sections like education, references, previousEmployment
      const updatedSection = [...formData[section]];
      
      // Extract the field name without the index
      const fieldName = name.replace(/\d+$/, ''); // Remove trailing numbers
      
      updatedSection[index] = {
        ...updatedSection[index],
        [fieldName]: type === "checkbox" ? checked : value,
      };
      setFormData({ ...formData, [section]: updatedSection });
    } else if (name.includes(".")) {
      // Handle nested objects like militaryService
      const [sectionName, fieldName] = name.split(".");
      setFormData({
        ...formData,
        [sectionName]: { ...formData[sectionName], [fieldName]: value },
      });
    } else {
      // Handle regular fields
      setFormData({ ...formData, [name]: value });
    }
  };

  const addEntry = (section) => {
    setFormData({
      ...formData,
      [section]: [
        ...formData[section],
        section === "references"
          ? { name: "", relationship: "", company: "", phone: "" }
          : section === "education"
          ? { degree: "", institution: "", yearCompleted: "" }
          : {
              company: "",
              supervisor: "",
              phone: "",
              startingSalary: "",
              endingSalary: "",
              responsibilities: "",
              startDate: "",
              endDate: "",
              reasonForLeaving: "",
              mayContact: false,
            },
      ],
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      let resumePath = "";
      if (resumeFile) {
        console.log("Original email:", formData.email);
        const safeEmail = formData.email.replace(/[^a-zA-Z0-9]/g, "_");
        console.log("Safe email:", safeEmail);
        const uploadPath = `uploads/${safeEmail}/${Date.now()}_${resumeFile.name}`;
        console.log("Upload path:", uploadPath);
        const resumeRef = ref(FIREBASE_STORAGE, uploadPath);
        await uploadBytes(resumeRef, resumeFile);
        resumePath = uploadPath; // Store the path instead of the URL
      }

      const applicationData = {
        ...formData,
        resumePath, // Store the path instead of the URL
        isGeneralApplication: true
      };

      const response = await fetch(`${API_URL}/applications`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(applicationData),
      });

      if (!response.ok) {
        const errorResponse = await response.json();
        throw new Error(
          `Failed to submit application: ${errorResponse.message}`
        );
      }

      setIsSubmitted(true);
      console.log("Application submitted successfully");
    } catch (error) {
      console.error("Error submitting application:", error);
      // Handle error (e.g., show error message to user)
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files[0]) {
      const file = e.target.files[0];
      // Check if file is larger than 5MB
      if (file.size > 5 * 1024 * 1024) {
        alert("File size must be less than 5MB");
        e.target.value = null; // Clear the file input
        return;
      }
      setResumeFile(file);
    }
  };

  const renderInput = (name, label, type = "text", icon, placeholder = "", required = false) => (
    <div className="mb-4">
      <label htmlFor={name} className="block mb-2 font-semibold text-gray-700">
        {label}
      </label>
      <div className="relative">
        {icon && (
          <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">
            {icon}
          </span>
        )}
        <input
          type={type}
          id={name}
          name={name}
          value={formData[name]}
          onChange={handleInputChange}
          placeholder={placeholder}
          className={`w-full p-3 ${
            icon ? "pl-10" : ""
          } border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
          required={required}
        />
      </div>
    </div>
  );

  const renderSection = (title, fields, sectionName, addEntry) => (
    <section className="mb-8 bg-white p-6 rounded-lg shadow-md">
      <h3 className="text-2xl font-semibold mb-4 flex items-center">
        {title === "Education" && <Book className="mr-2" />}
        {title === "References" && <User className="mr-2" />}
        {title === "Previous Employment" && <Briefcase className="mr-2" />}
        {title}
      </h3>
      {formData[sectionName].map((item, index) => (
        <div key={index} className="mb-6 p-4 border border-gray-200 rounded-lg">
          <h4 className="font-semibold mb-3 flex justify-between items-center">
            {`${title} #${index + 1}`}
            {formData[sectionName].length > 1 && (
              <button
                type="button"
                onClick={() => {
                  const updated = [...formData[sectionName]];
                  updated.splice(index, 1);
                  setFormData({ ...formData, [sectionName]: updated });
                }}
                className="text-red-500 hover:text-red-700"
              >
                <X size={20} />
              </button>
            )}
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {fields.map((field) => (
              <div key={field.name} className="mb-4">
                <label htmlFor={`${field.name}${index}`} className="block mb-2 font-semibold text-gray-700">
                  {field.label}
                </label>
                {field.type === "textarea" ? (
                  <textarea
                    id={`${field.name}${index}`}
                    name={`${field.name}${index}`}
                    value={item[field.name] || ""}
                    onChange={(e) => handleInputChange(e, index, sectionName)}
                    placeholder={field.placeholder}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    rows="3"
                  />
                ) : field.type === "checkbox" ? (
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id={`${field.name}${index}`}
                      name={`${field.name}${index}`}
                      checked={item[field.name] || false}
                      onChange={(e) => handleInputChange(e, index, sectionName)}
                      className="w-4 h-4 border border-gray-300 rounded bg-gray-50 focus:ring-3 focus:ring-blue-300"
                    />
                    <label htmlFor={`${field.name}${index}`} className="ml-2 text-sm text-gray-600">
                      {field.label}
                    </label>
                  </div>
                ) : (
                  <input
                    type={field.type}
                    id={`${field.name}${index}`}
                    name={`${field.name}${index}`}
                    value={item[field.name] || ""}
                    onChange={(e) => handleInputChange(e, index, sectionName)}
                    placeholder={field.placeholder}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
      <button
        type="button"
        onClick={() => addEntry(sectionName)}
        className="mt-2 flex items-center text-blue-600 hover:text-blue-800 transition-colors"
      >
        <Plus size={20} className="mr-1" /> Add Another {title}
      </button>
    </section>
  );

  return (
    <div className="flex flex-col min-h-screen bg-gray-100">
      <main className="flex-grow p-8">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold mb-6 text-center">
            General Job Application
          </h2>
          {isSubmitted ? (
            <div
              className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-6"
              role="alert"
            >
              <p className="font-bold">Success!</p>
              <p>Your application has been submitted successfully.</p>
            </div>
          ) : null}
          <form onSubmit={handleSubmit} className="space-y-8">
            <section className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-2xl font-semibold mb-4 flex items-center">
                <User className="mr-2" /> Applicant Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {renderInput("firstName", "First Name", "text", <User />)}
                {renderInput("lastName", "Last Name", "text", <User />)}
                {renderInput("email", "Email", "email", <Mail />)}
                {renderInput("phone", "Phone", "tel", <Phone />)}
                {renderInput(
                  "streetAddress",
                  "Street Address",
                  "text",
                  <MapPin />
                )}
                {renderInput(
                  "addressLine2",
                  "Address Line 2",
                  "text",
                  <MapPin />
                )}
                {renderInput("city", "City", "text", <Building />)}
                {renderInput(
                  "stateProvince",
                  "State/Province",
                  "text",
                  <MapPin />
                )}
                {renderInput(
                  "zipPostalCode",
                  "ZIP/Postal Code",
                  "text",
                  <MapPin />
                )}
                {renderInput("country", "Country", "text", <Globe />)}
              </div>
            </section>

            {renderSection(
              "Education",
              [
                {
                  name: "degree",
                  label: "Degree",
                  type: "text",
                  placeholder: "e.g., Bachelor of Science",
                },
                {
                  name: "institution",
                  label: "Institution",
                  type: "text",
                  placeholder: "e.g., University of Example",
                },
                {
                  name: "yearCompleted",
                  label: "Year Completed",
                  type: "number",
                  placeholder: "e.g., 2020",
                },
              ],
              "education",
              addEntry
            )}

            {renderSection(
              "References",
              [
                {
                  name: "name",
                  label: "Name",
                  type: "text",
                  placeholder: "Full Name",
                },
                {
                  name: "relationship",
                  label: "Relationship",
                  type: "text",
                  placeholder: "e.g., Former Manager",
                },
                {
                  name: "company",
                  label: "Company",
                  type: "text",
                  placeholder: "Company Name",
                },
                {
                  name: "phone",
                  label: "Phone",
                  type: "tel",
                  placeholder: "Contact Number",
                },
              ],
              "references",
              addEntry
            )}

            {renderSection(
              "Previous Employment",
              [
                {
                  name: "company",
                  label: "Company",
                  type: "text",
                  placeholder: "Company Name",
                },
                {
                  name: "supervisor",
                  label: "Supervisor",
                  type: "text",
                  placeholder: "Supervisor's Name",
                },
                {
                  name: "phone",
                  label: "Phone",
                  type: "tel",
                  placeholder: "Contact Number",
                },
                {
                  name: "startingSalary",
                  label: "Starting Salary",
                  type: "text",
                  placeholder: "e.g., $50,000",
                },
                {
                  name: "endingSalary",
                  label: "Ending Salary",
                  type: "text",
                  placeholder: "e.g., $60,000",
                },
                {
                  name: "responsibilities",
                  label: "Responsibilities",
                  type: "textarea",
                  placeholder: "Brief description of your role",
                },
                { name: "startDate", label: "Start Date", type: "date" },
                { name: "endDate", label: "End Date", type: "date" },
                {
                  name: "reasonForLeaving",
                  label: "Reason for Leaving",
                  type: "text",
                  placeholder: "Brief explanation",
                },
                {
                  name: "mayContact",
                  label: "May we contact this employer?",
                  type: "checkbox",
                },
              ],
              "previousEmployment",
              addEntry
            )}

            <section className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-2xl font-semibold mb-4 flex items-center">
                <Award className="mr-2" /> Military Service
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="mb-4">
                  <label htmlFor="militaryService.branch" className="block mb-2 font-semibold text-gray-700">
                    Branch
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">
                      <Award />
                    </span>
                    <input
                      type="text"
                      id="militaryService.branch"
                      name="militaryService.branch"
                      value={formData.militaryService.branch}
                      onChange={handleInputChange}
                      placeholder="e.g., Army, Navy, Air Force"
                      className="w-full p-3 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <label htmlFor="militaryService.datesServed" className="block mb-2 font-semibold text-gray-700">
                    Dates Served
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">
                      <Calendar />
                    </span>
                    <input
                      type="text"
                      id="militaryService.datesServed"
                      name="militaryService.datesServed"
                      value={formData.militaryService.datesServed}
                      onChange={handleInputChange}
                      placeholder="e.g., 2010-2014"
                      className="w-full p-3 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <label htmlFor="militaryService.rankAtDischarge" className="block mb-2 font-semibold text-gray-700">
                    Rank at Discharge
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">
                      <Award />
                    </span>
                    <input
                      type="text"
                      id="militaryService.rankAtDischarge"
                      name="militaryService.rankAtDischarge"
                      value={formData.militaryService.rankAtDischarge}
                      onChange={handleInputChange}
                      placeholder="e.g., Sergeant"
                      className="w-full p-3 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <label htmlFor="militaryService.typeOfDischarge" className="block mb-2 font-semibold text-gray-700">
                    Type of Discharge
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">
                      <FileText />
                    </span>
                    <input
                      type="text"
                      id="militaryService.typeOfDischarge"
                      name="militaryService.typeOfDischarge"
                      value={formData.militaryService.typeOfDischarge}
                      onChange={handleInputChange}
                      placeholder="e.g., Honorable"
                      className="w-full p-3 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
                <div className="col-span-2">
                  <label
                    htmlFor="militaryDischargeExplanation"
                    className="block mb-2 font-semibold text-gray-700"
                  >
                    If other than honorable, explain
                  </label>
                  <textarea
                    id="militaryDischargeExplanation"
                    name="militaryService.dischargeExplanation"
                    value={formData.militaryService.dischargeExplanation}
                    onChange={handleInputChange}
                    placeholder="Explanation if discharge was other than honorable"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    rows="4"
                  />
                </div>
              </div>
            </section>

            <section className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-2xl font-semibold mb-4 flex items-center">
                <File className="mr-2" /> Resume Upload
              </h3>
              <div className="mb-4">
                <label
                  htmlFor="resume"
                  className="block mb-2 font-semibold text-gray-700"
                >
                  Resume
                </label>
                <input
                  type="file"
                  id="resume"
                  name="resume"
                  onChange={handleFileChange}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  accept=".pdf,.doc,.docx"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Accepted formats: PDF, DOC, DOCX
                </p>
              </div>
            </section>

            <div className="mt-8 flex flex-col gap-4">
              <div className="w-full">
                <div className="flex items-start mb-4">
                  <div className="flex items-center h-5">
                    <input
                      id="terms"
                      name="terms"
                      type="checkbox"
                      onChange={(e) => setFormData({ ...formData, termsAccepted: e.target.checked })}
                      className="w-4 h-4 border border-gray-300 rounded bg-gray-50 focus:ring-3 focus:ring-blue-300"
                      required
                    />
                  </div>
                  <label htmlFor="terms" className="ml-2 text-sm text-gray-600">
                    I agree to the{" "}
                    <Link to="/terms" target="_blank" className="text-blue-600 hover:underline">
                      Terms of Service
                    </Link>{" "}
                    and{" "}
                    <Link to="/privacy" target="_blank" className="text-blue-600 hover:underline">
                      Privacy Policy
                    </Link>
                  </label>
                </div>
                {fieldErrors.terms && (
                  <p className="text-red-500 text-sm mt-1">{fieldErrors.terms}</p>
                )}
              </div>
              <button
                type="submit"
                disabled={isSubmitting || !formData.termsAccepted}
                className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400"
              >
                {isSubmitting ? "Submitting..." : "Submit Application"}
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

export default GeneralApplication;
